import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Progress,
  Tooltip,
  Button,
  Link,
} from '@chakra-ui/react'
import { LuTimer, LuSparkles, LuTriangleAlert, LuCheck, LuCalendar } from 'react-icons/lu'
import { format } from 'date-fns'
import { fr, enUS } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'

const PILOT_TOTAL_DAYS = 90

interface Props {
  accessExpiresAt: string | null | undefined
  userRole?: string
}

interface State {
  tone: 'teal' | 'orange' | 'red' | 'expired'
  daysLeft: number
  daysUsed: number
  percentUsed: number
  label: string
  headline: string
  subline: string
  icon: any
}

const computeState = (exp: Date): State => {
  const now = Date.now()
  const msLeft = exp.getTime() - now
  const daysLeft = Math.ceil(msLeft / (24 * 60 * 60 * 1000))
  const daysUsed = Math.max(0, PILOT_TOTAL_DAYS - daysLeft)
  const percentUsed = Math.min(100, Math.max(0, (daysUsed / PILOT_TOTAL_DAYS) * 100))

  if (daysLeft <= 0) {
    return {
      tone: 'expired',
      daysLeft: 0,
      daysUsed: PILOT_TOTAL_DAYS,
      percentUsed: 100,
      label: 'profile.pilotAccess.expiredLabel',
      headline: 'profile.pilotAccess.expiredHeadline',
      subline: 'profile.pilotAccess.expiredOn',
      icon: LuTriangleAlert,
    }
  }
  if (daysLeft <= 7) {
    return {
      tone: 'red',
      daysLeft,
      daysUsed,
      percentUsed,
      label: daysLeft === 1 ? 'profile.pilotAccess.oneDayLeft' : 'profile.pilotAccess.daysLeft',
      headline: 'profile.pilotAccess.endingSoon',
      subline: 'profile.pilotAccess.expiresOn',
      icon: LuTriangleAlert,
    }
  }
  if (daysLeft <= 30) {
    return {
      tone: 'orange',
      daysLeft,
      daysUsed,
      percentUsed,
      label: 'profile.pilotAccess.daysLeft',
      headline: 'profile.pilotAccess.title',
      subline: 'profile.pilotAccess.expiresOn',
      icon: LuTimer,
    }
  }
  return {
    tone: 'teal',
    daysLeft,
    daysUsed,
    percentUsed,
    label: 'profile.pilotAccess.daysLeft',
    headline: 'profile.pilotAccess.title',
    subline: 'profile.pilotAccess.expiresOn',
    icon: LuSparkles,
  }
}

const TONE_STYLES: Record<
  State['tone'],
  {
    gradientFrom: string
    gradientTo: string
    ring: string
    accent: string
    progressScheme: string
    numberColor: string
  }
> = {
  teal: {
    gradientFrom: 'teal.50',
    gradientTo: 'blue.50',
    ring: 'teal.200',
    accent: 'teal.500',
    progressScheme: 'teal',
    numberColor: 'teal.700',
  },
  orange: {
    gradientFrom: 'orange.50',
    gradientTo: 'yellow.50',
    ring: 'orange.200',
    accent: 'orange.500',
    progressScheme: 'orange',
    numberColor: 'orange.700',
  },
  red: {
    gradientFrom: 'red.50',
    gradientTo: 'pink.50',
    ring: 'red.200',
    accent: 'red.500',
    progressScheme: 'red',
    numberColor: 'red.700',
  },
  expired: {
    gradientFrom: 'gray.100',
    gradientTo: 'gray.50',
    ring: 'red.300',
    accent: 'red.500',
    progressScheme: 'red',
    numberColor: 'red.700',
  },
}

/**
 * "Votre accès pilote" — self-service jauge for invited users on a
 * time-bounded (90-day) pilot. Only rendered when User.accessExpiresAt
 * is a date; users on `accessExpiresAt = null` (unlimited pilots or
 * paid plans) don't see this panel.
 *
 * Shape mirrors the admin invitation badge thresholds
 * (renderAccessBadge in InvitationsAdmin.tsx) for consistency:
 *   >30 days → teal, 8-30 → orange, 1-7 → red, expired → red-solid.
 */
export default function PilotAccessPanel({ accessExpiresAt, userRole }: Props) {
  const { t } = useTranslation()
  const { i18n } = useTranslation()
  const dateLocale = i18n.language?.startsWith('en') ? enUS : fr
  // Admins / super_admins are never bounded by accessExpiresAt (the auth
  // middleware skips the expiry gate for them) — showing this panel would
  // be misleading, so hide unconditionally regardless of DB value.
  if (userRole === 'admin' || userRole === 'super_admin') return null
  if (!accessExpiresAt) return null
  const exp = new Date(accessExpiresAt)
  if (Number.isNaN(exp.getTime())) return null

  const state = computeState(exp)
  const styles = TONE_STYLES[state.tone]

  const start = new Date(exp.getTime() - PILOT_TOTAL_DAYS * 24 * 60 * 60 * 1000)

  const mailto = `mailto:support@careflai.com?subject=${encodeURIComponent(t('profile.pilotAccess.mailSubject'))}`

  return (
    <Box
      bgGradient={`linear(135deg, ${styles.gradientFrom}, ${styles.gradientTo})`}
      borderRadius="2xl"
      border="1px solid"
      borderColor={styles.ring}
      p={5}
      boxShadow="sm"
      position="relative"
      overflow="hidden"
    >
      {/* Decorative shine (top-right corner) */}
      <Box
        position="absolute"
        top="-40px"
        right="-40px"
        w="140px"
        h="140px"
        borderRadius="full"
        bg={styles.accent}
        opacity={0.08}
      />

      <HStack spacing={3} mb={4} position="relative">
        <Box
          w="42px"
          h="42px"
          borderRadius="xl"
          bg="white"
          display="grid"
          sx={{ placeItems: 'center' }}
          boxShadow="sm"
          border="1px solid"
          borderColor={styles.ring}
        >
          <Icon as={state.icon} boxSize={5} color={styles.accent} />
        </Box>
        <VStack align="start" spacing={0}>
          <Text fontWeight="700" fontSize="md" color="gray.800">
            {t(state.headline)}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {t('profile.pilotAccess.grantedFor', { count: PILOT_TOTAL_DAYS })}
          </Text>
        </VStack>
      </HStack>

      {/* Big number + label */}
      <HStack align="baseline" spacing={2} mb={3} position="relative">
        <Text fontSize="4xl" fontWeight="800" color={styles.numberColor} lineHeight="1">
          {state.daysLeft}
        </Text>
        <Text fontSize="sm" color="gray.600" fontWeight="600">
          {state.daysLeft <= 1 ? t('profile.pilotAccess.dayLeftUnit') : t('profile.pilotAccess.daysLeftUnit')}
        </Text>
      </HStack>

      {/* Progress bar with tooltip showing used vs total */}
      <Tooltip
        label={t('profile.pilotAccess.daysUsed', { used: state.daysUsed, total: PILOT_TOTAL_DAYS })}
        placement="top"
        hasArrow
      >
        <Box>
          <Progress
            value={state.percentUsed}
            size="sm"
            colorScheme={styles.progressScheme}
            borderRadius="full"
            bg="whiteAlpha.700"
            hasStripe={state.tone === 'red' || state.tone === 'expired'}
            isAnimated={state.tone === 'red' || state.tone === 'expired'}
          />
        </Box>
      </Tooltip>

      {/* Start / end dates */}
      <HStack
        justify="space-between"
        mt={2}
        fontSize="2xs"
        color="gray.500"
        fontWeight="500"
      >
        <HStack spacing={1}>
          <Icon as={LuCheck} boxSize={3} color={styles.accent} />
          <Text>{format(start, 'd MMM yyyy', { locale: dateLocale })}</Text>
        </HStack>
        <HStack spacing={1}>
          <Icon as={LuCalendar} boxSize={3} color={styles.accent} />
          <Text>{format(exp, 'd MMM yyyy', { locale: dateLocale })}</Text>
        </HStack>
      </HStack>

      {/* CTA for at-risk / expired states */}
      {(state.tone === 'red' || state.tone === 'expired') && (
        <Button
          as={Link}
          href={mailto}
          size="sm"
          colorScheme="red"
          variant={state.tone === 'expired' ? 'solid' : 'outline'}
          bg={state.tone === 'expired' ? 'red.500' : 'white'}
          mt={4}
          w="full"
          borderRadius="lg"
          _hover={{ textDecoration: 'none' }}
        >
          {state.tone === 'expired' ? t('profile.pilotAccess.reactivate') : t('profile.pilotAccess.extend')}
        </Button>
      )}
    </Box>
  )
}
